import * as z from "zod"

import type { Product,SaleConfig,UnitOfMeasurement } from "@/types"

export const formSchema = z.object({
  sellType: z.enum(["WEIGHT", "COUNT", "VOLUME"]),
  sellUomId: z.string().min(1, "Sell UOM is required"),
  minQuantity: z.coerce.number().min(0.01, "Min Quantity must be > 0"),
  maxQuantity: z.coerce.number().min(0.01, "Max Quantity must be > 0"),
  quantityStep: z.coerce.number().min(0.01),
  defaultQuantity: z.coerce.number().min(0.01),
  unitWeight: z.coerce.number().optional().nullable(),
  unitWeightUomId: z.string().optional().nullable(),
  pricingSchemeId: z.string().optional(),
  simplePriceCents: z.coerce.number().optional(),
  isActive: z.boolean().default(true),
  allowOnlineSale: z.boolean().default(true),
  isDiscountable: z.boolean().default(true),
  isDeliverable: z.boolean().default(true),
  isSample: z.boolean().default(false),
  isPromo: z.boolean().default(false),
}).superRefine((data, ctx) => {
  if (data.sellType === "COUNT" && data.unitWeight && data.unitWeight > 0) {
    if (!data.unitWeightUomId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unit Weight UOM is required if Unit Weight is set",
        path: ["unitWeightUomId"],
      })
    }
  }
  if (data.maxQuantity < data.minQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Max Quantity must be >= Min Quantity",
      path: ["maxQuantity"],
    })
  }
  if (data.defaultQuantity < data.minQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Default Quantity must be >= Min Quantity",
      path: ["defaultQuantity"],
    })
  }
  if (data.defaultQuantity > data.maxQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Default Quantity must be <= Max Quantity",
      path: ["defaultQuantity"],
    })
  }
})

export type SaleConfigFormValues = z.infer<typeof formSchema>

export const getDefaultValues = (data?: SaleConfig | null): SaleConfigFormValues => {
  if (data) {
    return {
      sellType: data.sellType || "WEIGHT",
      sellUomId: data.sellUomId || "",
      minQuantity: data.minQuantity ?? 1,
      maxQuantity: data.maxQuantity ?? 1,
      quantityStep: data.quantityStep ?? 1,
      defaultQuantity: data.defaultQuantity ?? 1,
      unitWeight: data.unitWeight ?? undefined,
      unitWeightUomId: data.unitWeightUomId || undefined,
      pricingSchemeId: data.pricingSchemeId || undefined,
      simplePriceCents: data.flatPriceCents ?? data.simplePriceCents ?? undefined,
      isActive: data.isActive ?? true,
      allowOnlineSale: data.allowOnlineSale ?? true,
      isDiscountable: data.isDiscountable ?? true,
      isDeliverable: data.isDeliverable ?? true,
      isSample: data.isSample ?? false,
      isPromo: data.isPromo ?? false,
    }
  }

  return {
    sellType: "WEIGHT",
    sellUomId: "",
    minQuantity: 1,
    maxQuantity: 1,
    quantityStep: 1,
    defaultQuantity: 1,
    unitWeight: undefined,
    unitWeightUomId: undefined,
    pricingSchemeId: undefined,
    simplePriceCents: undefined,
    isActive: true,
    allowOnlineSale: true,
    isDiscountable: true,
    isDeliverable: true,
    isSample: false,
    isPromo: false,
  }
}

interface GenerateDisplayNameInput {
  productData?: Product
  uoms: UnitOfMeasurement[]
  sellType: "WEIGHT" | "COUNT" | "VOLUME"
  sellUomId?: string
  unitWeight?: number | null
  unitWeightUomId?: string | null
}

export const generateDisplayName = ({
  productData,
  uoms,
  sellType,
  sellUomId,
  unitWeight,
  unitWeightUomId,
}: GenerateDisplayNameInput): string | undefined => {
  if (!productData) return undefined

  const productName = productData.name || ""
  let generatedName = ""

  if (productName) {
    const weightMatch = productName.match(/\b(\d+(\.\d+)?)\s*g\b/i)
    if (weightMatch) {
      const val = parseFloat(weightMatch[1])
      if (val === 1) generatedName = "Gram"
      else if (val === 3.5) generatedName = "3.5g Jar"
      else generatedName = `${val}g Jar`
    } else if (/eighth/i.test(productName)) generatedName = "3.5g Jar"
    else if (/quarter/i.test(productName)) generatedName = "Quarter Ounce"
    else if (/half\s+ounce/i.test(productName) || /\bhalf\b/i.test(productName)) generatedName = "Half Ounce"
    else if (/ounce/i.test(productName)) generatedName = "Ounce Pouch"
    else if (/(one|single)\s+pre-roll/i.test(productName)) generatedName = "Single Pre-Roll"
  }

  if (generatedName) return generatedName

  if (!sellUomId) return undefined
  const currentUom = uoms.find((u) => u.id === sellUomId)
  if (!currentUom) return undefined

  if (sellType === "WEIGHT") {
    generatedName = currentUom.name
    if (generatedName.endsWith("s")) generatedName = generatedName.slice(0, -1)
  } else if (sellType === "COUNT") {
    const weightUom = uoms.find((u) => u.id === unitWeightUomId)

    if (unitWeight && weightUom) {
      const weightShortForm = weightUom.shortForm.toLowerCase()
      const weightStr = `${unitWeight}${weightShortForm}`
      if (unitWeight === 3.5 && weightShortForm.includes("g")) generatedName = "3.5g Jar"
      else if (unitWeight === 1 && weightShortForm.includes("g")) generatedName = "1g Pre-Roll"
      else if (unitWeight === 28 && weightShortForm.includes("g")) generatedName = "Ounce Pouch"
      else generatedName = `${weightStr} Jar`
    } else {
      const catName = productData?.category?.name || "Item"
      generatedName = `Single ${catName}`
    }
  }

  return generatedName || undefined
}

interface GenerateSkuInput {
  productData?: Product
  uoms: UnitOfMeasurement[]
  unitWeight?: number | null
  unitWeightUomId?: string | null
  sellUomId?: string
}

export const generateSku = ({
  productData,
  uoms,
  unitWeight,
  unitWeightUomId,
  sellUomId,
}: GenerateSkuInput): string | undefined => {
  if (!productData) return undefined

  const categoryName = productData.category?.name || ""
  let catCode = categoryName.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4)

  if (/flower/i.test(categoryName)) catCode = "FLWR"
  else if (/edible/i.test(categoryName)) catCode = "EDBL"
  else if (/pre-roll|preroll/i.test(categoryName)) catCode = "PRER"
  else if (/vape|cartridge/i.test(categoryName)) catCode = "VAPE"
  else if (/concentrate|extract/i.test(categoryName)) catCode = "CONC"
  else if (/drink|beverage/i.test(categoryName)) catCode = "DRNK"
  else if (/tincture/i.test(categoryName)) catCode = "TNCT"
  else if (/topical/i.test(categoryName)) catCode = "TPCL"
  else if (/capsule|softgel/i.test(categoryName)) catCode = "CAPS"
  else if (/accessory|gear/i.test(categoryName)) catCode = "ACCS"
  else if (/merch/i.test(categoryName)) catCode = "MRCH"

  const productName = productData.name || "ITEM"
  const productSlug = productName
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")

  let sizePart = ""
  const weightUom = uoms.find((u) => u.id === unitWeightUomId)

  if (unitWeight && weightUom) {
    sizePart = `${unitWeight}${weightUom.shortForm.toUpperCase()}`
  } else {
    const sellUom = uoms.find((u) => u.id === sellUomId)
    if (sellUom) {
      sizePart = sellUom.shortForm.toUpperCase()
    }
  }

  sizePart = sizePart.replace(/\s+/g, "")
  const newSku = `${catCode}-${productSlug}-${sizePart}`
    .replace(/-+$/, "")
    .replace(/\s+/g, "")

  return newSku || undefined
}
